import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  apagarJson,
  editarJson,
  enviarFormulario,
  enviarJson,
  pedirJson,
} from "./api";

/* Horas, despesas e contratos de horas do chamado.

   Fica à parte de `tickets.ts` e `tickets-operacao.ts` para as frentes do atendimento não
   disputarem o mesmo arquivo. As consultas do chamado começam em `["tickets", id]`, então
   invalidar essa chave atualiza também a tela do chamado depois de um apontamento. */

const CHAVE = ["tickets"] as const;
const CHAVE_DOS_CONTRATOS = ["horas", "contratos"] as const;
const chaveDoChamado = (id: string) => [...CHAVE, id] as const;

/* ------------------------------------------------------------------ formatação */

/** `90` -> `1h30`; `45` -> `0h45`; negativo mantém o sinal (saldo estourado). */
export function formatarMinutos(minutos: number): string {
  const sinal = minutos < 0 ? "-" : "";
  const bruto = Math.abs(Math.round(minutos));
  return `${sinal}${Math.floor(bruto / 60)}h${String(bruto % 60).padStart(2, "0")}`;
}

/**
 * Lê uma duração digitada: `1h30`, `1:30`, `1,5` (horas), `90` (minutos) ou `2h`.
 * Devolve minutos inteiros, ou `null` quando não dá para entender ou não é positiva.
 */
export function lerDuracao(texto: string): number | null {
  const limpo = texto.trim().toLowerCase().replace(/\s+/g, "");
  if (!limpo) return null;
  const horasEMinutos = /^(\d+)(?:h|:)(\d{1,2})?(?:min|m)?$/.exec(limpo);
  if (horasEMinutos) {
    const minutos = Number(horasEMinutos[1]) * 60 + Number(horasEMinutos[2] ?? 0);
    return minutos > 0 ? minutos : null;
  }
  const decimal = /^(\d+)[.,](\d{1,2})$/.exec(limpo);
  if (decimal) {
    const minutos = Math.round(Number(`${decimal[1]}.${decimal[2]}`) * 60);
    return minutos > 0 ? minutos : null;
  }
  const soMinutos = /^(\d+)(?:min|m)?$/.exec(limpo);
  if (soMinutos) {
    const minutos = Number(soMinutos[1]);
    return minutos > 0 ? minutos : null;
  }
  return null;
}

/** `12550` -> `R$ 125,50`. */
export function formatarValor(centavos: number): string {
  return (centavos / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/** `125,50` / `1.250,00` / `125.5` -> centavos; `null` se não for um valor positivo. */
export function lerValor(texto: string): number | null {
  const limpo = texto.trim().replace(/^R\$\s*/i, "");
  if (!limpo) return null;
  const normal = /,/.test(limpo) ? limpo.replace(/\./g, "").replace(",", ".") : limpo;
  if (!/^\d+(\.\d{1,2})?$/.test(normal)) return null;
  const centavos = Math.round(Number(normal) * 100);
  return centavos > 0 ? centavos : null;
}

/** `125` -> `00:02:05`: o mostrador do cronômetro. */
export function formatarCronometro(segundos: number): string {
  const total = Math.max(0, Math.floor(segundos));
  const parte = (valor: number) => String(valor).padStart(2, "0");
  return `${parte(Math.floor(total / 3600))}:${parte(Math.floor((total % 3600) / 60))}:${parte(total % 60)}`;
}

/** O dia de hoje em Brasília, `AAAA-MM-DD`: o padrão do apontamento e da despesa. */
export function hojeEmBrasilia(agora: Date = new Date()): string {
  return agora.toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });
}

/* --------------------------------------------------------------------- horas */

const uuid = z.string().uuid();

const esquemaApontamento = z.object({
  id: uuid,
  agente_id: uuid,
  agente_nome: z.string(),
  estado: z.enum(["rodando", "pausado", "concluido"]),
  origem: z.enum(["manual", "cronometro"]),
  dia: z.string(),
  minutos: z.number().int().nullable(),
  segundos_decorridos: z.number().int().nonnegative(),
  retomado_em: z.string().nullable(),
  descricao: z.string(),
  consome_contrato: z.boolean(),
  contrato_id: uuid.nullable(),
  valor_hora_centavos: z.number().int().nullable(),
  criado_em: z.string(),
  meu: z.boolean(),
});

const esquemaHoras = z.object({
  apontamentos: z.array(esquemaApontamento),
  total_minutos: z.number().int(),
  despesas_total_centavos: z.number().int(),
});

const esquemaDespesa = z.object({
  id: uuid,
  agente_id: uuid,
  agente_nome: z.string(),
  valor_centavos: z.number().int(),
  descricao: z.string(),
  dia: z.string(),
  anexo_id: uuid.nullable(),
  criada_em: z.string(),
  meu: z.boolean(),
});

const esquemaDespesas = z.object({
  despesas: z.array(esquemaDespesa),
  total_centavos: z.number().int(),
});

export type Apontamento = z.infer<typeof esquemaApontamento>;
export type HorasDoChamado = z.infer<typeof esquemaHoras>;
export type Despesa = z.infer<typeof esquemaDespesa>;
export type DespesasDoChamado = z.infer<typeof esquemaDespesas>;

export const ROTULO_DO_APONTAMENTO: Record<Apontamento["estado"], string> = {
  rodando: "Cronômetro rodando",
  pausado: "Cronômetro pausado",
  concluido: "Concluído",
};

const caminhoDasHoras = (ticketId: string) => `/tickets/${encodeURIComponent(ticketId)}/horas`;
const caminhoDasDespesas = (ticketId: string) =>
  `/tickets/${encodeURIComponent(ticketId)}/despesas`;

export function useHorasDoChamado(ticketId: string, enabled = true) {
  return useQuery({
    queryKey: [...chaveDoChamado(ticketId), "horas"],
    queryFn: ({ signal }) => pedirJson(caminhoDasHoras(ticketId), esquemaHoras, { signal }),
    enabled: enabled && Boolean(ticketId),
  });
}

function useAtoDeHoras<T>(ticketId: string, enviar: (corpo: T) => Promise<HorasDoChamado>) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: enviar,
    onSuccess: (dados) => {
      cliente.setQueryData([...chaveDoChamado(ticketId), "horas"], dados);
      // O apontamento concluído mexe no saldo dos contratos e na linha do tempo do chamado.
      void cliente.invalidateQueries({ queryKey: CHAVE_DOS_CONTRATOS });
      void cliente.invalidateQueries({ queryKey: chaveDoChamado(ticketId), exact: true });
    },
  });
}

export interface PedidoDeApontamento {
  minutos: number;
  dia?: string;
  descricao: string;
  consome_contrato: boolean;
}

export function useApontarHoras(ticketId: string) {
  return useAtoDeHoras<PedidoDeApontamento>(ticketId, (corpo) =>
    enviarJson(caminhoDasHoras(ticketId), esquemaHoras, corpo),
  );
}

export function useIniciarCronometro(ticketId: string) {
  return useAtoDeHoras<{ descricao: string; consome_contrato: boolean }>(ticketId, (corpo) =>
    enviarJson(`${caminhoDasHoras(ticketId)}/cronometro`, esquemaHoras, corpo),
  );
}

export type AcaoDoCronometro = "pausar" | "retomar" | "parar";

export function useAcaoDoCronometro(ticketId: string) {
  return useAtoDeHoras<{ id: string; acao: AcaoDoCronometro }>(ticketId, ({ id, acao }) =>
    enviarJson(`${caminhoDasHoras(ticketId)}/${encodeURIComponent(id)}/${acao}`, esquemaHoras, {}),
  );
}

export function useApagarApontamento(ticketId: string) {
  return useAtoDeHoras<string>(ticketId, (id) =>
    apagarJson(`${caminhoDasHoras(ticketId)}/${encodeURIComponent(id)}`, esquemaHoras),
  );
}

/* ------------------------------------------------------------------ despesas */

export function useDespesasDoChamado(ticketId: string, enabled = true) {
  return useQuery({
    queryKey: [...chaveDoChamado(ticketId), "despesas"],
    queryFn: ({ signal }) => pedirJson(caminhoDasDespesas(ticketId), esquemaDespesas, { signal }),
    enabled: enabled && Boolean(ticketId),
  });
}

export interface PedidoDeDespesa {
  valor_centavos: number;
  descricao: string;
  dia?: string;
  comprovante?: File | null;
}

export function useLancarDespesa(ticketId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: PedidoDeDespesa) => {
      const formulario = new FormData();
      formulario.append("valor_centavos", String(corpo.valor_centavos));
      formulario.append("descricao", corpo.descricao);
      if (corpo.dia) formulario.append("dia", corpo.dia);
      if (corpo.comprovante) formulario.append("comprovante", corpo.comprovante);
      return enviarFormulario(caminhoDasDespesas(ticketId), esquemaDespesas, formulario);
    },
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaveDoChamado(ticketId) }),
  });
}

export function useApagarDespesa(ticketId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apagarJson(`${caminhoDasDespesas(ticketId)}/${encodeURIComponent(id)}`, esquemaDespesas),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaveDoChamado(ticketId) }),
  });
}

/* --------------------------------------------------------- contratos de horas */

const esquemaContrato = z.object({
  id: uuid,
  organizacao_id: uuid,
  nome: z.string(),
  minutos_contratados: z.number().int(),
  valor_hora_centavos: z.number().int(),
  inicio: z.string(),
  fim: z.string(),
  alerta_saldo_minutos: z.number().int().nullable(),
  alerta_enviado_em: z.string().nullable(),
  ativo: z.boolean(),
  consumido_minutos: z.number().int(),
  saldo_minutos: z.number().int(),
  vigente: z.boolean(),
});

const esquemaExtrato = z.object({
  contrato: esquemaContrato,
  valor_consumido_centavos: z.number().int(),
  linhas: z.array(
    z.object({
      apontamento_id: uuid,
      dia: z.string(),
      ticket_id: uuid,
      ticket_numero: z.number().int(),
      ticket_assunto: z.string(),
      agente_nome: z.string(),
      minutos: z.number().int(),
      descricao: z.string(),
      saldo_apos_minutos: z.number().int(),
    }),
  ),
});

const esquemaOrganizacao = z.object({
  id: uuid,
  razao_social: z.string(),
  nome_fantasia: z.string().nullable(),
});

export type ContratoDeHoras = z.infer<typeof esquemaContrato>;
export type ExtratoDoContrato = z.infer<typeof esquemaExtrato>;
export type OrganizacaoDaLista = z.infer<typeof esquemaOrganizacao>;

export function nomeDaOrganizacao(organizacao: OrganizacaoDaLista): string {
  return organizacao.nome_fantasia || organizacao.razao_social;
}

export function useOrganizacoesDoAtendimento(enabled = true) {
  return useQuery({
    queryKey: ["horas", "organizacoes"],
    queryFn: ({ signal }) =>
      pedirJson("/organizacoes?limite=200", z.array(esquemaOrganizacao), { signal }),
    enabled,
  });
}

export function useContratosDeHoras(enabled = true) {
  return useQuery({
    queryKey: CHAVE_DOS_CONTRATOS,
    queryFn: ({ signal }) =>
      pedirJson("/tickets/horas/contratos", z.array(esquemaContrato), { signal }),
    enabled,
  });
}

export interface PedidoDeContrato {
  organizacao_id: string;
  nome: string;
  minutos_contratados: number;
  valor_hora_centavos: number;
  inicio: string;
  fim: string;
  alerta_saldo_minutos: number | null;
}

export function useCriarContratoDeHoras() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: PedidoDeContrato) =>
      enviarJson("/tickets/horas/contratos", esquemaContrato, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DOS_CONTRATOS }),
  });
}

export function useAlterarContratoDeHoras() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: { id: string; corpo: Partial<PedidoDeContrato> & { ativo?: boolean } }) =>
      editarJson(`/tickets/horas/contratos/${encodeURIComponent(id)}`, esquemaContrato, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DOS_CONTRATOS }),
  });
}

export function useExtratoDoContrato(id: string | null) {
  return useQuery({
    queryKey: [...CHAVE_DOS_CONTRATOS, id, "extrato"],
    queryFn: ({ signal }) =>
      pedirJson(`/tickets/horas/contratos/${encodeURIComponent(id ?? "")}/extrato`, esquemaExtrato, {
        signal,
      }),
    enabled: Boolean(id),
  });
}
